import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { createHashRouter } from 'react-router-dom';
import { Loader } from 'src/@vymo/ui/atoms';
import FeatureFlagDevTool from 'src/featureFlags/featureFlagDevTool';
import { getUserAuthenticated } from 'src/models/auth/selectors';
import { getIsAuthenticated } from 'src/modules/authLegacy/selectors';
import { ModulesList } from 'src/modules/types';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import ChildComponent from './childComponent';
import ChildComponentWithRouter from './childComponentWithRouter';
import DialogWorkspace from './dialogWorkspace';
import { afterWorkspaceInit, beforeWorkspaceInit } from './lifecycle';
import {
  getModulesConfig,
  getPlatformPrefixPathFromHash,
  getRoutesConfig,
} from './queries';
import RecursiveWrapper from './recursiveWrapper';
import { getWorkspaceFullPage, getWorkspaceModules } from './selectors';
import { loadModules } from './slice';
import { isDevDebugMode, isLmsWeb } from './utils';
import '../designTokens/cssVariables.scss';
import '../designTokens/scssVariables.scss';
import styles from './index.module.scss';

interface HostApp {
  platform?: 'ios' | 'android' | 'web' | 'selfserve';
  moduleProps?: any;
  getClientConfig?: () => Record<string, any>;
  navigate?: (path: string, parm: any, query: any) => void;
  withoutRouter?: boolean;
  back?: () => void;
  reload?: () => void;
  location?: {
    path: string;
    pathParams: Record<string, string>;
    query: Record<string, string>;
  };
  visitorToken?: string;
  getUsersList?: any;
  route?: string;
  getNavigationItems?: any;
  closePortal?: (
    isLoginInit?: boolean,
    isInitSession?: boolean,
    authToken?: string,
  ) => void;
  initSession?: (authToken: string) => Promise<void>;
}

declare global {
  interface Window {
    hostApp?: HostApp | null;
    isWebPlatform?: boolean;
    moduleProps?: any;
  }
}

function Workspace({
  moduleList,
  layout,
  homePath = '/',
  loginPath,
  appName,
  beforeAppInitFunction = _.noop,
  afterAppInitFunction = _.noop,
  hostAppComponentProps = {},
}: {
  moduleList?: ModulesList;
  layout: any;
  homePath?: string;
  loginPath?: string;
  appName?: string;
  beforeAppInitFunction?: () => void;
  afterAppInitFunction?: () => void;
  hostAppComponentProps?: Record<string, any>;
}) {
  const [isBeforeInitDone, setIsBeforeInit] = useState(false);
  const [lifeCycleError, setLifeCycleError] = useState<Error>();

  const dispatch = useAppDispatch();

  const modulesConfig = useMemo(
    () => getModulesConfig(true, getPlatformPrefixPathFromHash(), moduleList),
    [moduleList],
  );

  const workspaceModules = useAppSelector(getWorkspaceModules);

  const fullPage = useAppSelector(getWorkspaceFullPage);

  const isLegacyAuthentciated = useAppSelector(getIsAuthenticated);

  const isUserAuthenticated = useAppSelector(getUserAuthenticated);

  const isAuthenticated = isLegacyAuthentciated || isUserAuthenticated;

  const isDebug = isDevDebugMode();

  useEffect(() => {
    dispatch(loadModules(modulesConfig));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const router = useMemo(() => {
    const moduleRoutes = getRoutesConfig(
      modulesConfig,
      homePath,
      isAuthenticated,
      loginPath,
      appName,
    ).map((moduleConfig) => {
      if (
        !['noModuleAdded', 'fallback', 'root'].includes(
          String(moduleConfig.code),
        )
      ) {
        const props =
          workspaceModules.find(({ code }) => code === moduleConfig.code)
            ?.props ?? {};
        // @ts-ignore
        moduleConfig.element = (
          <ChildComponentWithRouter id={moduleConfig.code} props={props} />
        );
      }
      return moduleConfig;
    });

    if (moduleRoutes.length > 0) {
      return createHashRouter(moduleRoutes);
    }
    return null;
  }, [
    appName,
    homePath,
    isAuthenticated,
    loginPath,
    modulesConfig,
    workspaceModules,
  ]);

  useEffect(() => {
    const initializeWorkspace = async () => {
      try {
        await beforeWorkspaceInit(beforeAppInitFunction);
        setIsBeforeInit(true);
        await afterWorkspaceInit(afterAppInitFunction);
      } catch (error: any) {
        setIsBeforeInit(true);
        setLifeCycleError(error);
      }
    };

    initializeWorkspace();
  }, [afterAppInitFunction, beforeAppInitFunction]);

  if (window.isCypressModule) {
    return (
      <>
        {layout}
        <DialogWorkspace />
      </>
    );
  }

  if (workspaceModules.length === 0) {
    return null;
  }

  if (lifeCycleError) {
    throw lifeCycleError;
  }

  if (!isBeforeInitDone) {
    return <Loader fullPage className={styles.workspace__loader} />;
  }
  if (isLmsWeb() && window?.hostApp?.withoutRouter) {
    return (
      <ChildComponent
        // @ts-ignore
        id={hostAppComponentProps?.id}
        props={hostAppComponentProps}
      />
    );
  }
  return (
    <>
      <RecursiveWrapper fullPage={fullPage} router={router}>
        {layout}
      </RecursiveWrapper>
      {isDebug && <FeatureFlagDevTool />}
      <DialogWorkspace />
    </>
  );
}

export default Workspace;
